import { computeQuality, qualityBand } from '@/lib/qualityScore';

/**
 * buildQualityNarrative(stock) — returns 1-3 plain-English bullets
 * explaining why a Quality Score came out the way it did, generated
 * from the same cached fundamentals the score itself uses.
 *
 * Each bullet: { tone: 'positive' | 'caution' | 'negative' | 'info',
 *                text: string }
 *
 * Mirrors the rule structure of the backend Triangle narrative
 * (see `_build_triangle_narrative` in server.py) but tuned to the
 * Quality Score's pillars: Safety (payout ratio), Yield, Growth,
 * Longevity (streak), and the Massive fundamentals delta. Capped
 * at 3 bullets so the panel stays scannable.
 *
 * Returns [] when the stock has no quality data — caller should hide
 * the section entirely in that case.
 */

function _pick(stock, ...keys) {
  for (const k of keys) {
    const v = stock?.[k];
    if (v === null || v === undefined) continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function _frac(stock, ...keys) {
  // Many cached fields can be stored as percent (15) or fraction
  // (0.15). Heuristic: anything ≤ 1.5 is a fraction.
  const v = _pick(stock, ...keys);
  if (v === null) return null;
  return v <= 1.5 ? v : v / 100;
}

function _isEtf(stock) {
  const t = (stock?.asset_type || '').toUpperCase();
  if (t === 'ETF') return true;
  const name = (stock?.name || '').toUpperCase();
  const hasFundName =
    / ETF /.test(` ${name} `) ||
    name.endsWith(' ETF') ||
    / FUND /.test(` ${name} `) ||
    / TRUST /.test(` ${name} `);
  return hasFundName && !(stock?.sector || '').trim();
}

export default function buildQualityNarrative(stock) {
  if (!stock) return [];
  const qResult = computeQuality(stock);
  // computeQuality returns {score, breakdown, ...} OR null. Pull just
  // the numeric score for narrative copy.
  const q = (qResult && typeof qResult === 'object') ? qResult.score : qResult;
  if (q === null || q === undefined) return [];
  const band = qualityBand(q);

  const bullets = [];

  const payout = _frac(stock, 'payout_ratio', 'quality_payout_ratio');
  const roic = _frac(stock, 'return_on_invested_capital', 'quality_roic');
  const roe = _frac(stock, 'return_on_equity', 'quality_roe');
  const fcf = _frac(stock, 'free_cash_flow_margin', 'quality_fcf_margin');
  const rev_stab = _pick(stock, 'revenue_stability', 'quality_revenue_stability');
  const d_to_e_raw = _pick(stock, 'debt_to_equity', 'quality_debt_to_equity');
  const d_ratio = d_to_e_raw === null ? null : (d_to_e_raw > 5 ? d_to_e_raw / 100 : d_to_e_raw);
  const yld = _pick(stock, 'dividend_yield');
  // Round 92o++++++++N++++++++++ (Jun 2026) — `dividend_yield` is
  // stored as a PERCENT VALUE in `stock_cache` (0.99 means 0.99%,
  // 3.5 means 3.5%, 9.58 means 9.58%). The legacy `_frac` heuristic
  // ("anything ≤ 1.5 is a fraction") wrongly re-interpreted yields
  // under 1.5% as huge fractions — FEX at 0.99% would render as
  // "99.0%" inside the dividend-trap banner. Always treat
  // `dividend_yield` as already-percent; mathematical operations
  // below have been updated to match.
  const streak = _pick(stock, 'dividend_streak_years', 'years_of_dividend_growth');
  const is_etf = _isEtf(stock);
  // Round 92n+++ — REIT/MLP carve-outs. GAAP EPS is structurally
  // depressed by non-cash depreciation on rental property / pipeline
  // assets, so EPS-based payout ratios > 100% are the norm, not a red
  // flag. We surface a neutral info bullet pointing at the Cash Lens
  // panel (FFO for REITs, DCF for MLPs) instead of the C-corp "red
  // flag" wording that misleads users.
  const is_reit = !!stock?.is_reit;
  const is_mlp = !!stock?.is_mlp;
  // Sep 2026 — BDC carve-out. BDCs distribute Net Investment Income
  // (NII), not GAAP earnings, so the GAAP-EPS payout ratio structurally
  // runs 100-150% and is a misleading "red flag" for BDC investors.
  // The correct sustainability metric is trailing NII coverage which
  // arrives on `stock.sec_coverage.ratio_current` (populated for every
  // BDC by the sec-coverage cron). We use it to silence the C-corp
  // "paying out more than earned" banner and surface NII coverage
  // in the same slot instead.
  const is_bdc = !!stock?.is_bdc;
  const bdcCov = (is_bdc && stock?.sec_coverage && stock.sec_coverage.status === 'ok')
    ? stock.sec_coverage : null;
  const bdcCovRatio = bdcCov ? Number(bdcCov.ratio_current) : null;
  const bdcCovOk = Number.isFinite(bdcCovRatio) && bdcCovRatio >= 0;
  // Sep 2026 — CEF carve-out. Like BDCs, CEFs distribute a mix of NII,
  // return of capital, realized gains and option premium — not GAAP
  // earnings. EPS payout > 100% is expected and is not by itself a
  // yield-trap signal. The actionable metrics are Premium/Discount to
  // NAV vs history (1-Year Z-Score) and the SEC coverage ratio when
  // available. We silence the C-corp red-flag bullet and route users
  // to the Z-score strip on the Insights tab instead.
  const is_cef = !!stock?.is_cef;
  const cefCov = (is_cef && stock?.sec_coverage && stock.sec_coverage.status === 'ok')
    ? stock.sec_coverage : null;
  const cefCovRatio = cefCov ? Number(cefCov.ratio_current) : null;
  const cefCovOk = Number.isFinite(cefCovRatio) && cefCovRatio >= 0;

  // ── Headline summary (always first) ──────────────────────────────
  // Single sentence: "Quality Score is 65 because:" followed by the
  // top 2-3 contributors. We pick the most informative drivers.
  const drivers = [];
  if (payout !== null) {
    const p = Math.round(payout * 100);
    // Sep 2026 — For BDCs, EPS-based payout > 100% is normal, so
    // "(over-distributing)" is misleading. Replace with a note that
    // NII coverage is the right metric — the targeted bullet below
    // shows the actual NII coverage number.
    if (is_bdc && p > 75) {
      if (bdcCovOk) {
        drivers.push(`NII cov ${bdcCovRatio.toFixed(2)}× (${bdcCovRatio >= 1.0 ? 'covered' : 'short'})`);
      } else {
        drivers.push(`NII coverage TBD`);
      }
    } else if (is_cef && p > 75) {
      // For CEFs, EPS payout is meaningless — surface a neutral token.
      drivers.push(`CEF payout (EPS-based, ignore)`);
    } else if (p > 100) drivers.push(`payout ${p}% (over-distributing)`);
    else if (p >= 75) drivers.push(`payout ${p}% (stretched)`);
    else if (p >= 25) drivers.push(`payout ${p}% (comfortable)`);
    else if (p > 0) drivers.push(`payout ${p}% (low)`);
  }
  if (roic !== null && roic * 100 >= 12) drivers.push(`ROIC ${(roic * 100).toFixed(0)}% (strong)`);
  if (fcf !== null && fcf * 100 >= 15) drivers.push(`FCF margin ${(fcf * 100).toFixed(0)}% (healthy)`);
  if (d_ratio !== null) {
    // REIT/MLP carve-out: 1.5-2.5× D/E is structurally normal for these
    // vehicle types (mortgaged property, capital-intensive pipelines).
    // Only call out elevated leverage when the ratio is well above that
    // structural baseline.
    if (is_reit || is_mlp) {
      if (d_ratio > 3) drivers.push(`D/E ${d_ratio.toFixed(2)} (high for sector)`);
      else if (d_ratio > 0) drivers.push(`D/E ${d_ratio.toFixed(2)} (typical for sector)`);
    } else if (d_ratio > 2) drivers.push(`D/E ${d_ratio.toFixed(2)} (elevated)`);
    else if (d_ratio > 0) drivers.push(`D/E ${d_ratio.toFixed(2)} (manageable)`);
  }
  if (streak !== null && streak >= 10) drivers.push(`${streak}+ yr streak`);
  if (yld !== null && yld > 0) {
    const y = yld;
    if (y >= 8) drivers.push(`yield ${y.toFixed(1)}% (high — verify safety)`);
    else if (y >= 2.5 && y <= 6) drivers.push(`yield ${y.toFixed(1)}% (sweet spot)`);
  }

  if (drivers.length > 0) {
    const bandLabel = (band && band.label) || '';
    bullets.push({
      tone: bandLabel === 'Elite' || bandLabel === 'Strong' || bandLabel === 'Healthy' ? 'positive'
            : bandLabel === 'Risky' ? 'negative'
            : bandLabel === 'Weak' ? 'caution'
            : 'info',
      text: `Quality Score is ${q}${bandLabel ? ` (${bandLabel})` : ''} because: ${drivers.slice(0, 4).join(', ')}.`,
    });
  }

  // ── Targeted-rule bullets follow ─────────────────────────────────
  // Skip payout for ETFs (their reported ratio is meaningless).
  if (!is_etf && payout !== null && payout > 0) {
    const p = Math.round(payout * 100);
    if (p > 100) {
      if (is_bdc) {
        // BDC-specific: swap the misleading C-corp payout warning for
        // the accurate NII coverage signal.
        if (bdcCovOk && bdcCovRatio >= 1.0) {
          bullets.push({
            tone: 'info',
            text: `Reported EPS payout is ${p}% — but BDCs distribute Net Investment Income (NII), not GAAP earnings, so an EPS payout above 100% is normal. Trailing NII coverage of ${bdcCovRatio.toFixed(2)}× shows the dividend IS being earned.`,
          });
        } else if (bdcCovOk && bdcCovRatio < 1.0) {
          bullets.push({
            tone: 'negative',
            text: `BDC NII coverage of ${bdcCovRatio.toFixed(2)}× — trailing Net Investment Income does NOT cover the dividend. This is the BDC-appropriate red flag; a cut is more likely than the EPS payout (${p}%) alone would suggest.`,
          });
        } else {
          bullets.push({
            tone: 'info',
            text: `Reported EPS payout is ${p}% — but BDCs distribute NII, not GAAP earnings, so this ratio is misleading. NII coverage data isn't available yet for this ticker; check Price/NAV in the Valuation Matrix panel below before assuming trap or bargain.`,
          });
        }
      } else if (is_cef) {
        // CEF-specific: same principle as BDCs but the sustainability
        // signal is Premium/Discount vs history (Z-score) rather than
        // NII coverage. When SEC coverage data IS available, surface
        // it as an extra data point.
        if (cefCovOk && cefCovRatio >= 1.0) {
          bullets.push({
            tone: 'info',
            text: `Reported EPS payout is ${p}% — but CEFs distribute a blend of NII, realized gains, option premium and return of capital, not GAAP earnings. SEC-reported coverage of ${cefCovRatio.toFixed(2)}× is the actionable metric; see also the 1-Year Z-Score on the Insights tab for premium/discount vs history.`,
          });
        } else {
          bullets.push({
            tone: 'info',
            text: `Reported EPS payout is ${p}% — but CEFs distribute a blend of NII, realized gains, option premium and return of capital, so this ratio doesn't measure sustainability. The actionable read for a CEF is the 1-Year Z-Score strip on the Insights tab (are you buying at a wider or narrower discount vs its own history?).`,
          });
        }
      } else if (is_reit) {
        bullets.push({
          tone: 'info',
          text: `Reported payout ratio is ${p}% — but REITs are required by law to distribute ≥90% of taxable income, and non-cash depreciation drags GAAP EPS. The FFO-based payout in the REIT Cash Lens (below) is the figure income investors actually use.`,
        });
      } else if (is_mlp) {
        bullets.push({
          tone: 'info',
          text: `Reported payout ratio is ${p}% — but MLPs measure their distribution against Distributable Cash Flow (DCF), not GAAP EPS. Heavy non-cash depreciation on pipelines makes EPS-based payout look stretched even when the cash distribution is well-covered.`,
        });
      } else {
        bullets.push({
          tone: 'negative',
          text: `Payout ratio is ${p}% — paying out more than the company earned. Verify cash flow and one-off items before assuming the dividend is sustainable; this is a red flag for a regular C-corp.`,
        });
      }
    } else if (p >= 75) {
      if (is_bdc) {
        if (bdcCovOk) {
          bullets.push({
            tone: bdcCovRatio >= 1.0 ? 'info' : 'caution',
            text: `EPS-based payout is ${p}% — this metric is misleading for a BDC. The right one is NII coverage, currently ${bdcCovRatio.toFixed(2)}× (${bdcCovRatio >= 1.0 ? 'dividend is earned' : 'dividend NOT earned'}).`,
          });
        } else {
          bullets.push({
            tone: 'info',
            text: `EPS-based payout is ${p}% — this metric is misleading for a BDC because distributions come from NII, not GAAP earnings. NII coverage data isn't available yet for this ticker.`,
          });
        }
      } else if (is_cef) {
        bullets.push({
          tone: 'info',
          text: `EPS-based payout is ${p}% — this metric doesn't apply cleanly to a CEF because distributions blend NII, realized gains, option premium and return of capital. Use the 1-Year Z-Score on the Insights tab instead.`,
        });
      } else if (is_reit || is_mlp) {
        const vehicle = is_reit ? 'REIT' : 'MLP';
        const cash_metric = is_reit ? 'FFO' : 'DCF';
        bullets.push({
          tone: 'info',
          text: `EPS-based payout ratio is ${p}% — this is structurally normal for a ${vehicle} because depreciation lowers GAAP EPS. The right metric is the ${cash_metric}-based payout in the Cash Lens panel.`,
        });
      } else {
        bullets.push({
          tone: 'caution',
          text: `Payout ratio of ${p}% leaves little room for hikes without earnings growth — watch the next two quarters of EPS.`,
        });
      }
    } else if (p >= 25 && p < 65) {
      bullets.push({
        tone: 'positive',
        text: `Payout ratio of ${p}% leaves comfortable room for both reinvestment and future dividend hikes.`,
      });
    }
  }

  if (streak !== null && streak >= 25) {
    bullets.push({
      tone: 'positive',
      text: `${streak}-year dividend-raise streak — Aristocrat-or-better track record. Boards almost never break a streak this long voluntarily.`,
    });
  } else if (streak !== null && streak >= 10) {
    bullets.push({
      tone: 'positive',
      text: `${streak}-year raise streak (Achiever tier) — consistent dividend culture, but not yet Aristocrat-protected.`,
    });
  } else if (streak !== null && streak === 0 && yld !== null && yld > 0) {
    // Oct 2026 — funds: a quarter-to-quarter "raise streak" is the wrong
    // lens (ETF payouts float with the holdings' dividends). Use the
    // server-stamped years paying (SCHD: since 2011) instead of a false
    // "short history" caution.
    const isFund = !!(stock?.is_etf || stock?.is_cef || stock?.is_mutual_fund);
    const yrsPaying = Number(stock?.fund_years_paying ?? stock?.years_paying) || 0;
    if (isFund && yrsPaying >= 3) {
      const since = stock?.fund_first_div_date ? ` (since ${String(stock.fund_first_div_date).slice(0, 4)})` : '';
      const cagr = Number(stock?.cagr_5yr ?? stock?.dividend_cagr_5y);
      bullets.push({
        tone: 'info',
        text: `Paying distributions for ${yrsPaying} years${since}. Fund payouts float with the holdings' dividends, so a raise streak isn't the right lens — ${Number.isFinite(cagr) && cagr !== 0 ? `the 5-yr distribution CAGR is ${cagr > 0 ? '+' : ''}${cagr.toFixed(1)}%` : 'check the 5-yr distribution CAGR'} on the Div Growth tab.`,
      });
    } else {
      bullets.push({
        tone: 'caution',
        text: `No multi-year raise streak yet — payer history is short or the dividend was cut/frozen recently. Verify it isn't a one-off special distribution.`,
      });
    }
  }

  // Round 93j (Jun 2026) — Suppress the high-yield "dividend-trap"
  // warning for fixed-rate assets (baby bonds, preferred shares,
  // depositary shares). Trap detection is calibrated against common-
  // stock dividend growth and EPS trends; a 10%+ yield on a $25-par
  // preferred trading at $12 is mathematically correct, not a trap.
  // The Stock Modal's amber "Asset Class" educational notice
  // explains the mechanics; firing a separate red trap warning on
  // top of that would be both wrong AND confusing.
  const isFixedRateAsset = (
    stock?.asset_class === 'baby_bond'
    || stock?.asset_class === 'preferred_share'
  );

  if (yld !== null && yld >= 8 && !isFixedRateAsset && stock?.pays_dividend !== false) {
    bullets.push({
      tone: 'caution',
      key: 'high-yield-trap',
      text: is_etf
        ? `Yield of ${yld.toFixed(2)}% is well above the dividend-trap threshold. For ETFs and closed-end funds, check the issuer's website for Section 19(a) notices to see how much of each distribution is Return of Capital vs net income, and watch the Insights tab → Recent Events for one-off special distributions or capital-gain payouts.`
        : `Yield of ${yld.toFixed(2)}% is well above the dividend-trap threshold — confirm with the Triangle Score and EPS trend before buying.`,
    });
  }

  if (d_ratio !== null && d_ratio > 2.0) {
    bullets.push({
      tone: 'caution',
      text: `Debt/Equity of ${d_ratio.toFixed(2)} is elevated — interest costs could pressure the dividend if rates stay high or earnings soften.`,
    });
  }

  if (stock?.is_money_market_fund) {
    // P158·9 (Feb 2026) — MMF-specific banner. Fires INSTEAD of the
    // ETF banner because MMFs aim to hold NAV at $1.00 and the
    // Triangle / Quality Score / DDM math simply doesn't apply.
    bullets.push({
      tone: 'info',
      key: 'mmf-explainer',
      text: `This is a Money Market Fund — it targets a constant $1.00 NAV and earns yield by holding short-dated Treasury / repo / government paper. Growth-oriented measures like the Triangle Score, DDM Target Buy, EPS, and payout ratio don't apply here. Check the 7-day yield and expense ratio on the Dividend Info tab instead.`,
    });
  } else if (is_etf) {
    bullets.push({
      tone: 'info',
      key: 'etf-explainer',
      text: `This is an ETF — the Quality Score reflects distribution growth + fund-level metrics only. Company-style measures like EPS, payout ratio, and ROIC don't apply here, so lean on the Triangle, the Dividend Info tab, and the Fund Profile (expense ratio + AUM) below to judge sustainability.`,
    });
  }

  if (rev_stab !== null && rev_stab > 0 && rev_stab <= 0.05) {
    bullets.push({
      tone: 'positive',
      text: `Revenue Stability of ${rev_stab.toFixed(2)} — sales are remarkably consistent year over year, exactly what a dependable dividend needs underneath it.`,
    });
  }

  return bullets.slice(0, 3);
}
